/**
 * TanStack Query keys for the conversation AI-control surface (state,
 * pause/resume/assign, goal progress). Lives outside any single feature
 * folder — both `features/ai-employees` (owns the state/goal queries + the
 * pause/resume/assign/complete mutations) and `features/inbox` (its
 * `useInboxSocket` realtime bus invalidates the goal query on
 * `inbox:goal:updated`) import this factory, so keeping it inside either
 * feature would create a cross-feature import in one direction or the other
 * — forbidden by `frontend/CLAUDE.md` ("Features are independent — no
 * cross-feature imports (extract to `shared/` if needed)").
 *
 * Originally lived in `features/ai-employees/constants.ts`; moved here
 * (SP2 Task 9 follow-up) once `useInboxSocket` needed to invalidate the goal
 * key, which would otherwise have made `features/inbox` depend on
 * `features/ai-employees` while `features/ai-employees` already depends on
 * `features/inbox` (via `inboxKeys`, used by `useAssignAgent`/`usePauseAi`/
 * `useResumeAi` to refresh the conversation-list AI mark) — a bidirectional
 * feature dependency.
 */
export const aiConversationKeys = {
    all: ['ai-conversations'] as const,
    state: (unifiedId: string) => ['ai-conversations', 'state', unifiedId] as const,
    /** Goal-progress query key (Task 9, spec §7) — one row per conversation. */
    conversationGoal: (unifiedId: string) => ['ai-conversations', 'goal', unifiedId] as const,
    /**
     * Supervised-mode (Autopilot Off) reply awaiting approval — cache value is
     * `IAiPendingReply | null`. Written by the query hook AND patched in place
     * by `useInboxSocket` on `inbox:ai:pending-reply` (same dual-writer shape
     * as `state`, which is why this factory lives outside both features).
     */
    pendingReply: (unifiedId: string) => ['ai-conversations', 'pending-reply', unifiedId] as const,
} as const;
